import { createRouter, createWebHistory } from "vue-router";
import CryptoJS from "crypto-js";

// Components
import dashboardView from "@/views/dashboard_one/dashboardView.vue";
import landingPage from "@/views/landing_view/landing_page.vue";
import loginView from "@/components/Login/LoginView.vue";
import ProfileSection from "@/components/Profile/ProfileSection.vue";
import EditProfile from "@/components/EditProfile/EditProfile.vue";
import ResetPassword from "@/components/ResetPassword/ResetPassword.vue";
import PasswordResetConfirm from "@/components/PasswordResetConfirm/PasswordResetConfirm.vue";
import PasswordResetComplete from "@/components/PasswordResetComplete/PasswordResetComplete.vue";
import OneTimePin from "@/components/OneTimePin/OneTimePin.vue";

const routes = [
  {
    path: "/home",
    name: "dashboard_view",
    component: dashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: "/",
    name: "landing_page",
    component: landingPage,
  },
  {
    path: "/login",
    name: "login",
    component: loginView,
  },
  {
    path: "/one_time_pin",
    name: "one_time_pin",
    component: OneTimePin,
  },
  {
    path: "/reset_password",
    name: "reset_password",
    component: ResetPassword,
  },
  {
    path: "/reset-password",
    alias: "/password_reset_confirm",
    name: "password_reset_confirm",
    component: PasswordResetConfirm,
  },
  {
    path: "/password_reset_complete",
    name: "password_reset_complete",
    component: PasswordResetComplete,
  },
  {
    path: "/profile",
    name: "profile",
    component: ProfileSection,
    meta: { requiresAuth: true }
  },
  {
    path: "/edit_profile",
    name: "edit_profile",
    component: EditProfile,
    meta: { requiresAuth: true }
  },
];

// Get encryption key from environment or use default fallback
const encryption_key = process.env.VUE_APP_ENCRYPTION_KEY || "default-secret-key-change-in-production";
const get_profile_url = process.env.VUE_APP_VIEW_USER_URL;

export function set_token(token) {
    if (!token) {
      throw new Error('Token is required');
    }
    var encrypted_token = CryptoJS.AES.encrypt(token, encryption_key).toString();
    sessionStorage.setItem("encrypted_token", encrypted_token);
}

export function get_token() {
  try {
    var encrypted_token = sessionStorage.getItem("encrypted_token");

    if (!encrypted_token) {
      return null;
    }

    var decrypted_token = CryptoJS.AES.decrypt(
      encrypted_token,
      encryption_key
    );

    var token = decrypted_token.toString(CryptoJS.enc.Utf8);
    
    if (!token) {
      throw new Error('Failed to decrypt token - possibly wrong encryption key');
    }

    return token;
  } catch (error) {
    return null;
  }
}

export function set_user_details(user_details) {
  try {
    var role = user_details.role_type || user_details.role || null;
    var user_id = user_details.user_id || null;
    var last_name = user_details.last_name || null;
    var first_name = user_details.first_name || null;

    sessionStorage.setItem("role", role);
    sessionStorage.setItem("user_id", user_id);
    sessionStorage.setItem("last_name", last_name);
    sessionStorage.setItem("first_name", first_name);
    
  } catch (error) {
    console.error('Error setting user details:', error);
  }
}

export function get_user_details() {
  var role = sessionStorage.getItem("role");
  var user_id = sessionStorage.getItem("user_id");
  var last_name = sessionStorage.getItem("last_name");
  var first_name = sessionStorage.getItem("first_name");

  var user_details = {
    role: role,
    user_id: user_id,
    last_name: last_name,
    first_name: first_name,
  };
  return user_details;
}

// NEW: Function to set complete user profile in sessionStorage
export function set_user_profile(user_profile) {
  try {
    const encrypted_profile = CryptoJS.AES.encrypt(
      JSON.stringify(user_profile), 
      encryption_key
    ).toString();
    sessionStorage.setItem("encrypted_user_profile", encrypted_profile);
  } catch (error) {
    console.error('Error setting user profile:', error);
  }
}

// NEW: Function to get complete user profile from sessionStorage
export function get_user_profile() {
  try {
    const encrypted_profile = sessionStorage.getItem("encrypted_user_profile");
    
    if (!encrypted_profile) {
      return null;
    }

    const decrypted_profile = CryptoJS.AES.decrypt(
      encrypted_profile,
      encryption_key
    );

    const profile_string = decrypted_profile.toString(CryptoJS.enc.Utf8);
    
    if (!profile_string) {
      return null;
    }

    return JSON.parse(profile_string);
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
}

// NEW: Function to fetch and store user profile globally
export async function fetch_and_store_user_profile() {
  try {
    const user_details = get_user_details();
    const user_id = user_details.user_id;
    const auth_token = get_token();

    if (!user_id || !auth_token || !get_profile_url) {
      return null;
    }

    const base_profile_url = get_profile_url.endsWith("/")
      ? get_profile_url
      : `${get_profile_url}/`;
    const profile_url = `${base_profile_url}${user_id}/`;

    const response = await fetch(profile_url, {
      method: 'GET',
      headers: {
        'Authorization': `Token ${auth_token}`,
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const user_profile = safe_extract_user_data(data);
    
    // Store the complete profile in sessionStorage
    set_user_profile(user_profile);
    
    return user_profile;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
}

// NEW: Safe extraction function for router
function safe_extract_user_data(apiResponse) {
  if (typeof apiResponse === 'string') {
    try {
      apiResponse = JSON.parse(apiResponse);
    } catch (e) {
      throw new Error('Invalid JSON response');
    }
  }
  
  let status;

  if (apiResponse.status !== undefined) {
    status = apiResponse.status;
  } else if (apiResponse['status'] !== undefined) {
    status = apiResponse['status'];
  }

  if (status && status !== 'success') {
    throw new Error(`API returned non-success status: ${status}`);
  }

  if (!status) {
    const direct_user = apiResponse.user || apiResponse.User || apiResponse;
    if (direct_user && (direct_user.id || direct_user.username || direct_user.email)) {
      return {
        ...direct_user,
        phone_number: direct_user.phone_number || direct_user.phoneNumber,
        is_first_login: direct_user.is_first_login !== undefined ? direct_user.is_first_login : direct_user.isFirstLogin,
        initial_complete_profile: direct_user.initial_complete_profile !== undefined ? direct_user.initial_complete_profile : direct_user.initialCompleteProfile
      };
    }
  }

  const data = apiResponse.data || apiResponse.Data || [];

  if (!Array.isArray(data) || data.length === 0) {
    throw new Error('No user data found in response');
  }

  const first_item = data[0];
  const user_object = first_item.user || first_item.User || first_item;

  if (!user_object) {
    throw new Error('User data not found in response');
  }

  const user_profile = {
    ...user_object,
    phone_number: first_item.phone_number || first_item.phoneNumber || user_object.phone_number,
    is_first_login: first_item.is_first_login !== undefined ? first_item.is_first_login : first_item.isFirstLogin,
    initial_complete_profile: first_item.initial_complete_profile !== undefined ? first_item.initial_complete_profile : first_item.initialCompleteProfile
  };

  return user_profile;
}

export function is_authenticated() {
  const token = sessionStorage.getItem("encrypted_token");
  return token !== null && token !== undefined && token !== '';
}

export function clear_token() {
  sessionStorage.removeItem("encrypted_token");
  sessionStorage.removeItem("encrypted_user_profile");
  sessionStorage.removeItem("role");
  sessionStorage.removeItem("user_id");
  sessionStorage.removeItem("last_name");
  sessionStorage.removeItem("first_name");
}

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// NEW: Enhanced navigation guard to fetch profile on auth routes
router.beforeEach(async (to, from, next) => {
  const isAuthenticated = is_authenticated();

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (isAuthenticated) {
      // Fetch and store user profile if not already available
      const existingProfile = get_user_profile();
      if (!existingProfile) {
        await fetch_and_store_user_profile();
      }
      next();
    } else {
      next("/login");
    }
  } else {
    next();
  }
});

export default router;